import React from 'react';
import { Alert, Content, HelperText, HelperTextItem, Label, List, ListItem } from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import type { CertificateResource } from '../../../types/models/OrganizationDomainResources';
import type { ResourceStepProps } from './resourceStep';

/** What the backend's `action` means for a certificate, in the user's words. */
const ACTION_LABEL: Record<string, { text: string; color: 'red' | 'orange' }> = {
  delete: { text: 'Order cancelled', color: 'orange' },
  revoke: { text: 'Revoked', color: 'red' },
};

/** Trailing-dot tolerant, case-insensitive. Both sides of every comparison go through this. */
const normalizeName = (name: string): string => name.trim().toLowerCase().replace(/\.$/, '');

/**
 * Whether a certificate name belongs to this organization domain — the frontend mirror of the
 * backend's matching rule (`organization-domains.md` → *Matching rule*). `*.sub.example.com` ends with
 * `.example.com`, so it counts as in scope, exactly as the backend classifies it.
 */
const coversDomain = (name: string, domainName: string): boolean => {
  const candidate = normalizeName(name);
  const domain = normalizeName(domainName);
  return candidate === domain || candidate === `*.${domain}` || candidate.endsWith(`.${domain}`);
};

/** Every name a certificate carries, CN first, deduplicated. */
const namesOf = (certificate: CertificateResource): string[] =>
  Array.from(new Set([certificate.cn, ...certificate.sans].map((name) => name.trim()).filter((name) => name !== '')));

/**
 * TLS-certificates step: pick which certificates go.
 *
 * A certificate is matched to the domain by its **name strings**, not by a foreign key, so a single row
 * can carry names outside this domain — revoking it would break those too. That is the one thing this
 * step owes the user beyond the list, and it is stated for the current selection rather than in general.
 *
 * Two different actions live in one table: a not-yet-issued ZeroSSL row is **cancelled**, an OPNsense
 * trust row is **revoked**, and everything else is forced-kept with its reason (issued certificates can
 * be neither revoked nor deleted). The backend's `action` decides, never the platform name.
 */
const TlsCertificatesStep: React.FunctionComponent<ResourceStepProps> = ({
  domain,
  resources,
  elements,
  selectedKeys,
  onToggle,
  onToggleAll,
}) => {
  const { certificates, platforms_scanned: platformsScanned } = resources.tls_certificates;
  const byId = new Map(certificates.map((certificate) => [certificate.id, certificate]));
  const selectableRows = elements.filter((element) => element.deletable);
  const selectedRows = selectableRows.filter((element) => selectedKeys.has(element.key));
  const allSelected = selectableRows.length > 0 && selectedRows.length === selectableRows.length;
  const someSelected = selectedRows.length > 0 && !allSelected;

  // Names of *selected* certificates that this domain does not cover: deleting or revoking those rows
  // takes the other names with them.
  const collateral = selectedRows
    .map((element) => {
      const certificate = byId.get(element.id);
      if (certificate === undefined) {
        return null;
      }
      const outside = namesOf(certificate).filter((name) => !coversDomain(name, domain.name));
      return outside.length === 0 ? null : { label: element.label, outside };
    })
    .filter((entry): entry is { label: string; outside: string[] } => entry !== null);

  if (elements.length === 0) {
    return (
      <Content component="p">
        No certificate for {domain.name} was found on the {platformsScanned}{' '}
        {platformsScanned === 1 ? 'certificate platform' : 'certificate platforms'} available to this organization, so
        nothing is removed here.
      </Content>
    );
  }

  return (
    <div>
      <Content component="p" className="pf-v6-u-mb-md">
        These certificates carry a name under <strong>{domain.name}</strong>. Everything you leave unticked is kept and
        stays valid.
      </Content>

      <Table aria-label={`Certificates of ${domain.name}`} variant="compact">
        <Thead>
          <Tr>
            <Th
              select={{
                onSelect: (_event, isSelecting) => onToggleAll(isSelecting),
                isSelected: allSelected,
                isIndeterminate: someSelected,
                // `isHeaderSelectDisabled` is a no-op in react-table 6.6.0 — `isDisabled` is the one
                // that reaches the input.
                isDisabled: selectableRows.length === 0,
              }}
              aria-label="Select all certificates"
            />
            <Th>Certificate</Th>
            <Th>Platform</Th>
            <Th>What happens</Th>
            <Th>Details</Th>
          </Tr>
        </Thead>
        <Tbody>
          {elements.map((element, rowIndex) => {
            const certificate = byId.get(element.id);
            const action = element.action === null ? null : (ACTION_LABEL[element.action] ?? null);
            const extraNames = certificate === undefined ? [] : namesOf(certificate).slice(1);
            return (
              <Tr key={element.key}>
                <Td
                  select={{
                    rowIndex,
                    isSelected: selectedKeys.has(element.key),
                    isDisabled: !element.deletable,
                    onSelect: (_event, isSelecting) => onToggle(element.key, isSelecting),
                  }}
                />
                <Td dataLabel="Certificate">
                  {certificate?.cn ?? element.label}
                  {extraNames.length > 0 && (
                    <HelperText>
                      <HelperTextItem>{`Also covers ${extraNames.join(', ')}`}</HelperTextItem>
                    </HelperText>
                  )}
                  {/* A disabled row checkbox cannot carry a tooltip (react-table hard-disables the
                      input), so the reason is visible text. */}
                  {element.blockedReason !== null && (
                    <HelperText>
                      <HelperTextItem>{element.blockedReason}</HelperTextItem>
                    </HelperText>
                  )}
                </Td>
                <Td dataLabel="Platform">{certificate?.platform_name ?? '—'}</Td>
                <Td dataLabel="What happens">
                  {action === null ? (
                    <Label isCompact color="grey">
                      Kept
                    </Label>
                  ) : (
                    <Label isCompact color={action.color}>
                      {action.text}
                    </Label>
                  )}
                </Td>
                <Td dataLabel="Details">{element.detail ?? '—'}</Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>

      {collateral.length > 0 && (
        <Alert
          variant="warning"
          isInline
          component="h2"
          title="Some selected certificates also cover other names"
          className="pf-v6-u-mt-md"
        >
          <p>
            These certificates were matched on a name under {domain.name}, but they secure other names too. Those stop
            being served as well.
          </p>
          <List aria-label="Names outside this domain">
            {collateral.map((entry) => (
              <ListItem key={entry.label}>{`${entry.label} — ${entry.outside.join(', ')}`}</ListItem>
            ))}
          </List>
        </Alert>
      )}

      {selectableRows.length === 0 && (
        <Alert variant="info" isInline component="h2" title="None of these can be removed" className="pf-v6-u-mt-md">
          <p>
            Issued certificates cannot be revoked or deleted on their platform, and a pending request can only be
            cancelled where the platform supports it. They are listed so you know they stay.
          </p>
        </Alert>
      )}

      <Content component="p" className="pf-v6-u-mt-md pf-v6-u-text-color-subtle pf-v6-u-font-size-sm">
        A revoked certificate stops being trusted immediately and cannot be reinstated. A certificate you keep renews
        automatically only while Ancalagon&apos;s DNS zone for {domain.name} stays in place.
      </Content>
    </div>
  );
};

export default TlsCertificatesStep;
